'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildBreadcrumbManager } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { analytics } from '@lib/analytics/page-events';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TreeAccordion } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { OwnershipFacetProps } from './OwnershipFacet.types';

const PARENT_SELECTOR = '[data-test="search-result-section"]';

export const OwnershipFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: OwnershipFacetProps) => {
  const session = useSession();
  const searchParams = useSearchParams();
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const title =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.ownership')?.value ??
    'Ownership';

  const label =
    searchValuesMicrocopy?.find(
      (m) => m.key === 'search-ui-values.hide-owned-assets'
    )?.value ?? 'Hide owned assets';

  // Local state initialised from URL, updated on toggle
  const [checked, setChecked] = useState(
    () => searchParams?.get('hideOwnership') === 'true'
  );

  // Sync local state if URL changes externally (e.g. browser back/forward)
  useEffect(() => {
    const fromUrl = searchParams?.get('hideOwnership') === 'true';
    setChecked(fromUrl);
  }, [searchParams]);

  const eng = useEngine();
  const breadcrumbManager = useMemo(() => buildBreadcrumbManager(eng), [eng]);

  // Clear ownership filter when "Clear All" breadcrumbs is clicked
  useEffect(() => {
    let prevHasBreadcrumbs = breadcrumbManager.state.hasBreadcrumbs;

    const unsubscribe = breadcrumbManager.subscribe(() => {
      const hasBreadcrumbs = breadcrumbManager.state.hasBreadcrumbs;
      if (prevHasBreadcrumbs && !hasBreadcrumbs) {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.get('hideOwnership')) {
          analytics.pushFilterEvent({
            filter_action: 'deselect',
            filter_action_category: 'ownership',
            filter_action_label: 'hide_owned_assets',
          });
          currentUrl.searchParams.delete('hideOwnership');
          window.history.pushState({}, '', currentUrl);
          setChecked(false);
        }
      }
      prevHasBreadcrumbs = hasBreadcrumbs;
    });

    return unsubscribe;
  }, [breadcrumbManager]);

  // DOM-based hiding of owned assets
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.querySelector(PARENT_SELECTOR) as HTMLElement | null;
    if (!el) return;
    if (checked) {
      el.setAttribute('data-hide-owned', 'true');
    } else {
      el.removeAttribute('data-hide-owned');
    }
  }, [checked]);

  const treeItems = useMemo(() => [{ id: 'ownership', label }], [label]);

  const selectedIds = useMemo(() => (checked ? ['ownership'] : []), [checked]);

  const handleSelectMany = useCallback((_ids: string[], selected: boolean) => {
    if (typeof window === 'undefined') return;
    const currentUrl = new URL(window.location.href);
    analytics.pushFilterEvent({
      filter_action: selected ? 'select' : 'deselect',
      filter_action_category: 'ownership',
      filter_action_label: 'hide_owned_assets',
    });
    if (selected) {
      currentUrl.searchParams.set('hideOwnership', 'true');
    } else {
      currentUrl.searchParams.delete('hideOwnership');
    }
    window.history.pushState({}, '', currentUrl);
    setChecked(selected);
    const el = document.querySelector(PARENT_SELECTOR) as HTMLElement | null;
    if (selected) {
      el?.setAttribute('data-hide-owned', 'true');
    } else {
      el?.removeAttribute('data-hide-owned');
    }
  }, []);

  if (session.status !== 'authenticated') {
    return null;
  }

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={variant === 'mobile'}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  return (
    <FacetAccordion
      title={title}
      variant={variant === 'horizontal' ? 'sidebar' : variant}
      className={className}
      defaultOpen={variant !== 'drawer'}
      selectedCount={checked ? 1 : 0}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
